/**
 * Moved here from `apps/client/src/lib/` in the menus rework, wave 1 — word
 * for word, behaviour unchanged (ADR 0025's back-gesture semantics, and the
 * wave 3b desync fixes). The "review" findings and file names below are
 * TwinTen's history, kept because they are why the code is shaped this way.
 *
 * A tiny History API-backed stack for sheets, sub-screens and the game
 * screen — so the Android back gesture closes the top overlay (or asks
 * before leaving a run) instead of leaving the app or the installed PWA
 * outright (review §3.1, F3.4: "no History API anywhere in `apps/client/src`
 * … in the installed PWA back closes the app").
 *
 * ## The shape of the problem
 *
 * Every sheet and sub-screen in this app is a boolean or union `$state`
 * owned by whichever component opened it — there is no router, and adding
 * one just for this would be a much bigger change than the bug warrants.
 * What they all have in common is *when* they open and close, so this module
 * hooks exactly that moment rather than routing anything.
 *
 * ## The two ways an overlay closes, and why the return value matters
 *
 * {@link pushOverlay} pushes one `history` entry and registers `onClose` to
 * run when the browser (a back gesture, the hardware/software back button)
 * pops it. But an overlay can also close because a person tapped its own
 * "Done"/"Back" button — and that must consume the same history entry, or
 * the *next* back press would land on a screen the player already left. The
 * function `pushOverlay` returns is exactly that: call it when the overlay
 * closes on its own, and it walks the pushed entry back off the stack. If
 * the entry was already consumed by an actual `popstate` (the stack no
 * longer has this closer in it), calling the returned function is a no-op —
 * which is what makes it safe to call unconditionally from a component's
 * `onMount` cleanup, however the overlay came to close.
 *
 * ## Why a UI-close never calls `history.back()` synchronously
 *
 * The obvious implementation — splice the closer out and call
 * `history.back()` right there — has a real browser-observed failure mode
 * (review finding: caught live in `back-navigation.spec.ts` against an
 * actual Chromium, not a theory). Two overlays can close-and-open in the
 * very same synchronous render for reasons that have nothing to do with each
 * other — the Game menu opening a sheet in its place, or picking "New Game"
 * closing the picker *and* mounting the game screen's own back-guard in one
 * click handler — and when a UI-close's `history.back()` fires before that
 * *other* overlay's `pushOverlay` runs its `history.pushState()`, Chromium
 * was observed to silently drop that `pushState` — `history.length` simply
 * never grows, with no exception to catch. The two ever after disagree about
 * how deep the stack is, and a later real back gesture pops the wrong thing
 * or nothing at all.
 *
 * The fix is to never let a UI-close's real navigation race a same-tick
 * `pushState` at all: {@link pushOverlay}'s closer only ever removes its
 * entry from the in-memory `stack` synchronously (so lookups stay correct
 * immediately), and defers the *real* `history.back()`/`history.go()` past
 * the closing overlay's own component-close cascade — always, not only when
 * it detects a collision.
 *
 * That defer is a `setTimeout(…, 0)`, not a `queueMicrotask`. It was a
 * microtask first, and it was not enough: the *other* overlay in the race
 * (a sheet the Game menu is opening in its own place, or the game screen
 * mounting because "New Game" both closed the picker and started a run) does
 * not necessarily call `pushOverlay` before the current synchronous script
 * finishes — Svelte's own reactivity can settle a state change like
 * `pickerOpen = false` across more than one microtask turn of its own before
 * the component that state change unmounts actually runs its cleanup, and a
 * *second* state change from the very same click handler (mounting a
 * different screen) is not guaranteed to resolve within that same handful of
 * turns either. A microtask callback queued eagerly, at the moment the first
 * overlay closes, can run *before* that reactivity has gotten around to
 * mounting the second one — this was caught live, in a real Chromium, not a
 * theory. `setTimeout(…, 0)` always runs after the entire microtask queue
 * has drained, however many turns any framework's own scheduling took,
 * which is what actually guarantees this module sees every `pushOverlay`
 * call the same user gesture was going to make before it settles anything.
 * `pushOverlay`'s own `history.pushState()` stays synchronous throughout —
 * component code (and this module's own tests) can keep relying on a fresh
 * overlay's entry existing the instant it opens.
 */

type Closer = () => void;

const stack: Closer[] = [];
/**
 * Overlays opened while this module's own walk back was in flight (after
 * `reconcile` called `history.back()`, before its `popstate` landed). Their
 * entries are pushed once the walk lands: a `pushState` inside that window is
 * clobbered by the pending traversal in Chromium — the browser lands on the
 * entry the back aimed at — which left the stack one entry deeper than the
 * browser and swallowed the next real Back (the co-op exits review, ADR 0059:
 * a confirmation closing just before the menu armed its tab guard).
 */
const deferred: Closer[] = [];
/**
 * How many real `popstate` events this module's *own* navigations
 * (`reconcile`, below) still owe — decremented as they arrive, so a real
 * back gesture arriving in between is never mistaken for one of these.
 */
let suppressCount = 0;
/**
 * Real browser history entries that no longer have a `stack` entry to
 * answer for them — every overlay {@link pushOverlay}'s closer has removed
 * from `stack` but not yet actually navigated away from. Paid off by
 * {@link reconcile} in one navigation, once the synchronous burst that ran
 * it up has finished mutating `stack` (see the module doc for why this is
 * deferred at all).
 */
let owedPops = 0;
let reconcileScheduled = false;
let installed = false;

/** Callers of {@link whenHistorySettled} still waiting for the stack to settle. */
const settleWaiters: (() => void)[] = [];

/** Nothing owed, nothing scheduled, no walk back in flight, nothing waiting to be pushed. */
function isSettled(): boolean {
  return owedPops === 0 && !reconcileScheduled && suppressCount === 0 && deferred.length === 0;
}

function releaseWaiters(): void {
  const waiting = settleWaiters.splice(0, settleWaiters.length);
  for (const resolve of waiting) resolve();
}

function notifyIfSettled(): void {
  if (isSettled()) releaseWaiters();
}

function handlePopState(): void {
  if (suppressCount > 0) {
    suppressCount -= 1;
    if (suppressCount === 0) pushDeferred();
    notifyIfSettled();
    return;
  }
  if (owedPops > 0) {
    // This real entry was never `stack`'s to answer for — `reconcile` just
    // hasn't gotten to removing it yet.
    owedPops -= 1;
    return;
  }
  const closer = stack.pop();
  if (closer !== undefined) closer();
}

/**
 * Clears {@link owedPops} in one navigation. Scheduled by every closer that
 * runs during the same still-settling burst of state changes via
 * `setTimeout(…, 0)` (see the module doc for why not a microtask), but only
 * the first of those callbacks to actually fire still finds `owedPops > 0` —
 * the rest are no-ops, which is what makes this free to coalesce any number
 * of same-burst closes into a single `history.go`.
 */
function reconcile(): void {
  reconcileScheduled = false;
  if (owedPops === 0) {
    notifyIfSettled();
    return;
  }
  const delta = owedPops;
  owedPops = 0;
  suppressCount += 1;
  try {
    if (delta === 1) history.back();
    else history.go(-delta);
  } catch {
    suppressCount -= 1;
    owedPops += delta;
    // Nothing more will happen on its own: let anyone waiting go on.
    releaseWaiters();
  }
}

function scheduleReconcile(): void {
  if (reconcileScheduled) return;
  reconcileScheduled = true;
  setTimeout(reconcile, 0);
}

function ensureInstalled(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('popstate', handlePopState);
}

/**
 * Call when an overlay opens — a sheet, a sub-screen, or (from `App.svelte`)
 * a whole screen entered from the menu. Pushes one `history` entry and
 * registers `onClose` to run when a back gesture pops it.
 *
 * Returns a function to call when the overlay closes through its own UI —
 * see the module doc for why this matters, why it is safe to call more than
 * once or after a back gesture already closed the overlay, and why the real
 * navigation it causes is deferred rather than immediate.
 */
export function pushOverlay(onClose: Closer): Closer {
  ensureInstalled();
  if (suppressCount > 0) {
    // A walk back of our own is in flight: push once it lands (`deferred`).
    deferred.push(onClose);
  } else {
    pushEntry(stack.length + 1);
  }
  stack.push(onClose);

  return () => {
    const index = stack.lastIndexOf(onClose);
    if (index === -1) return; // Already popped by an actual back gesture.
    stack.splice(index, 1);
    // Closed before its entry was ever pushed: there is nothing to give back.
    const waiting = deferred.indexOf(onClose);
    if (waiting !== -1) {
      deferred.splice(waiting, 1);
      return;
    }
    owedPops += 1;
    scheduleReconcile();
  };
}

/**
 * The key every entry of this stack carries in `history.state`
 * (`{ uiOverlay: true, depth }`), for a test that waits for an entry, or a
 * router that must tell the shell's entries from its own. Neutral since
 * 1.2.0; the 1.1 name, `nmOverlay`, which entries carried beside it until
 * 2.0.0, is gone. Read it with {@link isOverlayEntry}.
 */
export const OVERLAY_STATE_KEY = 'uiOverlay';

/**
 * Whether `state` (a `history.state`) is one of the shell's overlay entries
 * (`uiOverlay: true`). Since 2.0.0 an entry that carries only the 1.1 key,
 * `nmOverlay`, is not one: no shell has written such an entry since 1.1.0.
 */
export function isOverlayEntry(state: unknown): boolean {
  if (typeof state !== 'object' || state === null) return false;
  return (state as { readonly uiOverlay?: unknown }).uiOverlay === true;
}

function pushEntry(depth: number): void {
  try {
    history.pushState({ [OVERLAY_STATE_KEY]: true, depth }, '');
  } catch {
    // A sandboxed or storage-restricted context can refuse pushState. The
    // overlay still opens; there is just nothing for the back gesture to do
    // differently here than it already would.
  }
}

/** The walk back landed: the entries of the overlays opened meanwhile go in now. */
function pushDeferred(): void {
  const waiting = deferred.splice(0, deferred.length);
  const base = stack.length - waiting.length;
  waiting.forEach((_closer, i) => {
    pushEntry(base + i + 1);
  });
}

/** Options for {@link whenHistorySettled}. */
export interface HistorySettleOptions {
  /**
   * Resolve anyway after this long (ms; default 2000). A walk back whose
   * `popstate` never arrives (a browser that refused the traversal) must not
   * hang a router forever; past this the stack is as settled as it will get.
   */
  readonly timeoutMs?: number;
}

/**
 * Resolves once the history stack has settled: every overlay closed through
 * its UI has had its entry walked back, that walk's `popstate` has landed,
 * and every overlay opened meanwhile has had its entry pushed.
 *
 * What it is for (Interlock ADR 0047, gap 1): a game that changes the URL or
 * pushes an entry of its own right after closing an overlay in the same click
 * — a router — must wait for this first, or the deferred walk back would land
 * on the entry it just made. A test waits on it instead of on `popstate`.
 *
 * It never resolves synchronously: it first lets the current task finish
 * (one macrotask), because the overlay a click just closed only gives its
 * entry back once the framework has unmounted it, which is after the click
 * handler returns. So it is safe to call straight after `open = false`.
 *
 * Adds no behaviour to the stack.
 */
export function whenHistorySettled(options: HistorySettleOptions = {}): Promise<void> {
  const timeoutMs = options.timeoutMs ?? 2000;
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, timeoutMs);
    setTimeout(() => {
      if (isSettled()) finish();
      else settleWaiters.push(finish);
    }, 0);
  });
}

/** Test-only: forget every registered overlay and uninstall the listener. */
export function resetHistoryStack(): void {
  if (installed) window.removeEventListener('popstate', handlePopState);
  installed = false;
  suppressCount = 0;
  owedPops = 0;
  reconcileScheduled = false;
  stack.length = 0;
  deferred.length = 0;
  releaseWaiters();
}
